// client/src/components/OrderTable.jsx
import React from "react";
import StatusBadge from "./StatusBadge";
import { formatEasternDateTime } from "../utils/timezone";

const formatDate = (iso) =>
  formatEasternDateTime(iso, {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

const formatCurrency = (amount, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(parseFloat(amount));

const Th = ({ children, align = "left", sortKey, sortState, onSort }) => {
  const isActive = !!(sortKey && sortState?.key === sortKey);
  const direction = isActive ? sortState?.dir : null;

  return (
    <th
      onClick={() => sortKey && onSort && onSort(sortKey)}
      style={{
        padding: "12px 16px",
        textAlign: align,
        fontSize: 11,
        fontWeight: 700,
        color: isActive ? "#334155" : "#94a3b8",
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        borderBottom: "2px solid #e8ecf0",
        background: "#f8fafc",
        cursor: sortKey ? "pointer" : "default",
        userSelect: "none",
        whiteSpace: "nowrap",
      }}
    >
      {children}
      {sortKey && (
        <span style={{ marginLeft: 4, opacity: isActive ? 1 : 0.3 }}>
          {direction === "asc" ? "↑" : direction === "desc" ? "↓" : "↕"}
        </span>
      )}
    </th>
  );
};

const Td = ({ children, align = "left", muted = false, style: extra = {} }) => (
  <td style={{
    padding: "14px 16px",
    textAlign: align,
    fontSize: 13,
    color: muted ? "#94a3b8" : "#1e293b",
    verticalAlign: "middle",
    ...extra
  }}>
    {children}
  </td>
);

const OrderTable = ({ orders, loading, sort, onSort, onSelectOrder, selectedId }) => {

  if (loading) {
    return (
      <div style={{ background: "white", borderRadius: 16, border: "1px solid #e8ecf0", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {["Order", "Date", "Customer", "Products", "Qty", "Shipped", "Fulfillment", "Location ID", "Total"].map(h => (
                <th key={h} style={{ padding: "12px 16px", background: "#f8fafc", borderBottom: "2px solid #e8ecf0" }}>
                  <div style={{ height: 12, background: "#e8ecf0", borderRadius: 4, width: "60%" }} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...Array(6)].map((_, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                {[...Array(9)].map((_, j) => (
                  <td key={j} style={{ padding: "14px 16px" }}>
                    <div style={{ height: 13, background: "#f1f5f9", borderRadius: 4, width: `${40 + (j * 7) % 50}%`, animation: "pulse 1.5s ease infinite" }} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (!orders.length) {
    return (
      <div style={{
        background: "white", borderRadius: 16, border: "1px solid #e8ecf0",
        padding: "64px 32px", textAlign: "center",
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
        <h3 style={{ margin: "0 0 8px", color: "#0f172a", fontSize: 18 }}>No orders found</h3>
        <p style={{ margin: 0, color: "#94a3b8", fontSize: 14 }}>Try adjusting your filters or refresh the data.</p>
      </div>
    );
  }

  return (
    <div style={{ background: "white", borderRadius: 16, border: "1px solid #e8ecf0", overflow: "hidden" }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 960 }}>
          <thead>
            <tr>
              <Th sortKey="order_number" sortState={sort} onSort={onSort}>Order</Th>
              <Th sortKey="order_date" sortState={sort} onSort={onSort}>Date</Th>
              <Th sortKey="customer" sortState={sort} onSort={onSort}>Customer</Th>
              <Th sortKey="products" sortState={sort} onSort={onSort}>Products</Th>
              <Th align="center" sortKey="qty" sortState={sort} onSort={onSort}>Qty</Th>
              <Th align="center">
                <span title="Real quantity shipped by ShipHero — distinct from ordered Qty; a cosmetic ShipHero close (e.g. location disabled for import) can still say Fulfilled with 0 here">Shipped</span>
              </Th>
              <Th sortKey="fulfillment_status" sortState={sort} onSort={onSort}>Fulfillment</Th>
              <Th sortKey="location" sortState={sort} onSort={onSort}>Location ID</Th>
              <Th sortKey="last_import_at" sortState={sort} onSort={onSort}>Imported</Th>
              <Th align="right" sortKey="total_price" sortState={sort} onSort={onSort}>Total</Th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order, idx) => {
              const isSelected = selectedId === order.id;
              const productNames = order.line_items
                .slice(0, 2)
                .map((i) => i.title)
                .join(", ");
              const moreProducts = order.line_items.length > 2 ? ` +${order.line_items.length - 2} more` : "";

              return (
                <tr
                  key={order.id}
                  onClick={() => onSelectOrder(order)}
                  style={{
                    borderBottom: "1px solid #f1f5f9",
                    cursor: "pointer",
                    background: isSelected ? "#f0f9ff" : idx % 2 === 0 ? "white" : "#fafbfc",
                    transition: "background 0.12s",
                    borderLeft: isSelected ? "3px solid #0ea5e9" : "3px solid transparent",
                  }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "#f8fafc"; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = idx % 2 === 0 ? "white" : "#fafbfc"; }}
                >
                  <Td>
                    <span style={{ fontWeight: 800, color: "#0f172a", fontSize: 14, fontFamily: "monospace" }}>
                      {order.order_number}
                    </span>
                  </Td>
                  <Td muted style={{ whiteSpace: "nowrap" }}>
                    {formatDate(order.created_at)}
                  </Td>
                  <Td>
                    <div style={{ fontWeight: 600, color: "#1e293b" }}>{order.customer.full_name}</div>
                    <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{order.customer.email}</div>
                  </Td>
                  <Td>
                    <div style={{ maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      <span style={{ color: "#334155" }}>{productNames}</span>
                      {moreProducts && <span style={{ color: "#94a3b8", fontSize: 12 }}>{moreProducts}</span>}
                    </div>
                  </Td>
                  <Td align="center">
                    <span style={{
                      display: "inline-block",
                      background: "#f1f5f9", borderRadius: 8,
                      padding: "3px 10px", fontWeight: 800,
                      color: "#334155", fontSize: 13,
                    }}>
                      {order.total_quantity}
                    </span>
                  </Td>
                  <Td align="center">
                    {order.shipped_quantity === null || order.shipped_quantity === undefined ? (
                      <span style={{ color: "#cbd5e1", fontSize: 12, fontStyle: "italic" }}>—</span>
                    ) : (
                      <span style={{
                        display: "inline-block",
                        background: order.shipped_quantity < order.total_quantity ? "#fef2f2" : "#f0fdf4",
                        color: order.shipped_quantity < order.total_quantity ? "#991b1b" : "#166534",
                        border: `1px solid ${order.shipped_quantity < order.total_quantity ? "#fecaca" : "#bbf7d0"}`,
                        borderRadius: 8,
                        padding: "3px 10px", fontWeight: 800, fontSize: 13,
                      }}>
                        {order.shipped_quantity}
                      </span>
                    )}
                  </Td>
                  <Td>
                    <StatusBadge status={order.fulfillment_status} />
                  </Td>
                  <Td>
                    {order.location_id ? (
                      <span style={{
                        background: order.location_mismatch ? "#fef2f2" : "#f0fdf4",
                        color: order.location_mismatch ? "#991b1b" : "#166534",
                        border: `1px solid ${order.location_mismatch ? "#fecaca" : "#bbf7d0"}`,
                        borderRadius: 6,
                        padding: "3px 8px", fontSize: 11, fontWeight: 600,
                      }}>
                        {order.location_name || order.location_id}
                      </span>
                    ) : order.validation_checked ? (
                      <span style={{ color: "#94a3b8", fontSize: 11, fontStyle: "italic" }}>N/A</span>
                    ) : (
                      <span style={{ color: "#cbd5e1", fontSize: 11 }}>Pending</span>
                    )}
                  </Td>
                  <Td muted style={{ whiteSpace: "nowrap", fontSize: 11 }}>
                    {order.order_import_date ? formatDate(order.order_import_date) : (
                      <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>—</span>
                    )}
                  </Td>
                  <Td align="right">
                    <span style={{ fontWeight: 800, color: "#0f172a", fontSize: 15 }}>
                      {formatCurrency(order.total_price, order.currency)}
                    </span>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Table footer */}
      <div style={{
        padding: "12px 20px",
        background: "#f8fafc",
        borderTop: "1px solid #e8ecf0",
        fontSize: 12,
        color: "#94a3b8",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}>
        <span>Showing {orders.length} order{orders.length !== 1 ? "s" : ""}</span>
        <span>Click a row to view details</span>
      </div>
    </div>
  );
};

export default OrderTable;
